// Experimental: Beautiful PPTX export for Get Who To Buy Strategy
// Uses native PPTX text elements (not images) with beautiful dark theme design
import pptxgen from 'pptxgenjs';

// Helper to parse markdown links and return text runs with hyperlink options
const parseTextWithLinks = (text) => {
  if (!text) return [{ text: '' }];
  const parts = [];
  let lastIndex = 0;
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = markdownLinkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index), options: {} });
    }

    const label = match[1];
    const url = match[2];
    parts.push({
      text: label,
      options: {
        hyperlink: {
          url: url,
          tooltip: url
        },
        color: '1E88E5', // Blue color for links
        underline: true
      }
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    if (remainingText.trim()) {
      parts.push({ text: remainingText, options: {} });
    }
  }

  // If no links found, return as plain text
  if (parts.length === 0) {
    return [{ text: text || '', options: {} }];
  }

  return parts;
};

// Helper to add text with links to a slide
const addTextWithLinks = (slide, text, options) => {
  const parts = parseTextWithLinks(text);

  // If no links, use simple addText
  if (parts.length === 1 && !parts[0].options.hyperlink) {
    slide.addText(parts[0].text, { ...options, ...parts[0].options });
    return;
  }

  // Build array of text runs for links (pptxgenjs format: array of objects)
  const textRuns = parts.map((part) => {
    const runOptions = {
      fontSize: options.fontSize || 12,
      color: part.options.hyperlink ? part.options.color : options.color || 'D4D4D8',
      ...part.options
    };

    // Remove undefined properties
    Object.keys(runOptions).forEach((key) => {
      if (runOptions[key] === undefined) delete runOptions[key];
    });

    return {
      text: part.text,
      options: runOptions
    };
  });

  // Use addText with array format for hyperlinks
  slide.addText(textRuns, options);
};

// Helper to add logo branding to top right of slide (all on one line)
const addLogoBranding = (slide) => {
  // Powered by text - much wider box to prevent wrapping
  slide.addText('Powered by', {
    x: 6.5,
    y: 0.2,
    w: 1.8,
    h: 0.3,
    fontSize: 10,
    color: colors.textMuted,
    align: 'right',
    fontFace: 'Arial',
    shrinkText: false,
    wrap: false
  });

  // Logo (favicon.svg) - positioned right after "Powered by"
  slide.addImage({
    path: '/favicon.svg',
    x: 8.35,
    y: 0.21,
    w: 0.3,
    h: 0.3
  });

  // Askpot text - positioned right after logo
  slide.addText('Askpot', {
    x: 8.7,
    y: 0.2,
    w: 1.3,
    h: 0.3,
    fontSize: 13,
    color: colors.textMain,
    bold: true,
    align: 'left',
    fontFace: 'Arial'
  });
};

// Color palette matching the dark theme design
const colors = {
  bgMain: '000000', // Black background
  textMain: 'FFFFFF', // White main text
  textMuted: 'A1A1AA', // zinc-400
  textBody: 'D4D4D8', // zinc-300
  bgCard: '18181B', // zinc-900
  borderCard: '27272A', // zinc-800
  textBodyCard: 'E4E4E7', // zinc-200
  textNumber: '71717A', // zinc-600
  accent: '1E88E5' // Blue accent
};

// Shape type constants
const ShapeType = pptxgen.shapes || {
  RECTANGLE: 'rect',
  ELLIPSE: 'ellipse'
};

// Render all slides using native PPTX elements (not images)
export async function exportGetWhoToBuyPptx2(strategyData, fileName = 'get-who-to-buy-strategy-v2.pptx') {
  const pptx = new pptxgen();
  pptx.layout = 'LAYOUT_16x9'; // 16:9 aspect ratio

  // Slide 1: Title
  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: colors.bgMain };

  // Logo branding
  addLogoBranding(titleSlide);

  // ASKPOT title
  titleSlide.addText('ASKPOT', {
    x: 0.5,
    y: 2.5,
    w: 9,
    h: 1.0,
    fontSize: 72,
    color: colors.textMain,
    align: 'center',
    bold: true,
    fontFace: 'Arial'
  });

  // Subtitle
  const subtitle = strategyData.client ? `Strategic Framework for ${strategyData.client}` : 'Strategic Brief 2025';
  titleSlide.addText(subtitle, {
    x: 0.5,
    y: 3.6,
    w: 9,
    h: 0.4,
    fontSize: 18,
    color: colors.textMuted,
    align: 'center',
    fontFace: 'Arial'
  });

  // Category
  if (strategyData.category) {
    titleSlide.addText(strategyData.category, {
      x: 0.5,
      y: 4.1,
      w: 9,
      h: 0.3,
      fontSize: 14,
      color: colors.textNumber,
      align: 'center',
      fontFace: 'Arial'
    });
  }

  // Slide 2-5: GET / WHO / TO / BY
  const frameworkSections = [
    { label: 'GET', content: strategyData.get },
    { label: 'WHO', content: strategyData.who },
    { label: 'TO', content: strategyData.to },
    { label: 'BY', content: strategyData.by }
  ].filter((s) => s.content);

  frameworkSections.forEach((section) => {
    const slide = pptx.addSlide();
    slide.background = { color: colors.bgMain };

    // Logo branding
    addLogoBranding(slide);

    // Large heading
    slide.addText(section.label, {
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 0.6,
      fontSize: 48,
      color: colors.textMain,
      bold: true,
      fontFace: 'Arial'
    });

    // Content with links
    addTextWithLinks(slide, section.content, {
      x: 0.5,
      y: 1.3,
      w: 9,
      h: 4.0,
      fontSize: 16,
      color: colors.textBody,
      valign: 'top',
      wrap: true,
      lineSpacing: 24,
      fontFace: 'Arial'
    });
  });

  // Slide 6: Key Strategic Insight
  if (strategyData.key_strategic_insight) {
    const slide = pptx.addSlide();
    slide.background = { color: colors.bgMain };

    // Logo branding
    addLogoBranding(slide);

    slide.addText('Key Strategic Insight', {
      x: 0.5,
      y: 0.4,
      w: 9,
      h: 0.5,
      fontSize: 32,
      color: colors.textMain,
      bold: true,
      fontFace: 'Arial'
    });

    // Card background
    slide.addShape(ShapeType.RECTANGLE, {
      x: 0.5,
      y: 1.1,
      w: 9,
      h: 4.2,
      fill: { color: colors.bgCard },
      line: { color: colors.borderCard, width: 1 }
    });

    // Content with links
    addTextWithLinks(slide, strategyData.key_strategic_insight, {
      x: 0.8,
      y: 1.4,
      w: 8.4,
      h: 3.6,
      fontSize: 16,
      color: colors.textBodyCard,
      valign: 'top',
      wrap: true,
      lineSpacing: 22,
      fontFace: 'Arial'
    });
  }

  // Slide 7: Creative Implication
  if (strategyData.creative_implication) {
    const slide = pptx.addSlide();
    slide.background = { color: colors.bgMain };

    // Logo branding
    addLogoBranding(slide);

    slide.addText('Creative Implication', {
      x: 0.5,
      y: 0.4,
      w: 9,
      h: 0.5,
      fontSize: 32,
      color: colors.textMain,
      bold: true,
      fontFace: 'Arial'
    });

    // Content with links
    addTextWithLinks(slide, strategyData.creative_implication, {
      x: 0.5,
      y: 1.1,
      w: 9,
      h: 4.2,
      fontSize: 16,
      color: colors.textBody,
      valign: 'top',
      wrap: true,
      lineSpacing: 22,
      fontFace: 'Arial'
    });
  }

  // Slide 8+: Supporting Data Points
  if (strategyData.supporting_data_points && strategyData.supporting_data_points.length > 0) {
    const itemsPerSlide = 4;
    const totalSlides = Math.ceil(strategyData.supporting_data_points.length / itemsPerSlide);

    for (let i = 0; i < totalSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: colors.bgMain };

      // Logo branding
      addLogoBranding(slide);

      slide.addText('Supporting Data Points', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 32,
        color: colors.textMain,
        bold: true,
        fontFace: 'Arial'
      });

      const startIdx = i * itemsPerSlide;
      const pageItems = strategyData.supporting_data_points.slice(startIdx, startIdx + itemsPerSlide);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageItems.length;

      pageItems.forEach((item, idx) => {
        // Number - smaller size
        slide.addText(String(startIdx + idx + 1).padStart(2, '0'), {
          x: 0.5,
          y: yPos,
          w: 0.6,
          h: itemHeight - 0.1,
          fontSize: 20,
          color: colors.textNumber,
          bold: true,
          valign: 'top',
          fontFace: 'Arial'
        });

        // Content with links
        addTextWithLinks(slide, item, {
          x: 1.2,
          y: yPos,
          w: 8.3,
          h: itemHeight - 0.1,
          fontSize: 12,
          color: colors.textBody,
          valign: 'top',
          wrap: true,
          lineSpacing: 18,
          fontFace: 'Arial'
        });

        yPos += itemHeight;
      });
    }
  }

  // Slide 9+: Tone & Channel Direction
  if (strategyData.tone_channel_direction) {
    const entries = Object.entries(strategyData.tone_channel_direction).filter((e) => e[1]);
    const itemsPerSlide = 2;
    const totalSlides = Math.ceil(entries.length / itemsPerSlide);

    for (let i = 0; i < totalSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: colors.bgMain };

      // Logo branding
      addLogoBranding(slide);

      slide.addText('Tone & Channel Direction', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 32,
        color: colors.textMain,
        bold: true,
        fontFace: 'Arial'
      });

      const startIdx = i * itemsPerSlide;
      const pageEntries = entries.slice(startIdx, startIdx + itemsPerSlide);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageEntries.length;

      pageEntries.forEach(([key, value]) => {
        const title = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');

        // Card background
        slide.addShape(ShapeType.RECTANGLE, {
          x: 0.5,
          y: yPos,
          w: 9,
          h: itemHeight - 0.1,
          fill: { color: colors.bgCard },
          line: { color: colors.borderCard, width: 1 }
        });

        // Title
        slide.addText(title, {
          x: 0.7,
          y: yPos + 0.1,
          w: 8.6,
          h: 0.3,
          fontSize: 16,
          color: colors.accent,
          bold: true,
          fontFace: 'Arial'
        });

        // Content with links
        addTextWithLinks(slide, value, {
          x: 0.7,
          y: yPos + 0.5,
          w: 8.6,
          h: itemHeight - 0.6,
          fontSize: 12,
          color: colors.textBodyCard,
          valign: 'top',
          wrap: true,
          lineSpacing: 18,
          fontFace: 'Arial'
        });

        yPos += itemHeight;
      });
    }
  }

  // Slide 10+: Strategic Guardrails
  if (strategyData.strategic_guardrails) {
    const entries = Object.entries(strategyData.strategic_guardrails).filter((e) => e[1]);
    const itemsPerSlide = 2;
    const totalSlides = Math.ceil(entries.length / itemsPerSlide);

    for (let i = 0; i < totalSlides; i++) {
      const slide = pptx.addSlide();
      slide.background = { color: colors.bgMain };

      // Logo branding
      addLogoBranding(slide);

      slide.addText('Strategic Guardrails', {
        x: 0.5,
        y: 0.4,
        w: 9,
        h: 0.5,
        fontSize: 32,
        color: colors.textMain,
        bold: true,
        fontFace: 'Arial'
      });

      const startIdx = i * itemsPerSlide;
      const pageEntries = entries.slice(startIdx, startIdx + itemsPerSlide);

      let yPos = 1.1;
      const itemHeight = (5.625 - 1.1 - 0.3) / pageEntries.length;

      pageEntries.forEach(([key, value]) => {
        const title = key
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');

        // Left border accent (using shape)
        slide.addShape(ShapeType.RECTANGLE, {
          x: 0.5,
          y: yPos,
          w: 0.05,
          h: itemHeight - 0.1,
          fill: { color: 'D32F2F' },
          line: { color: 'transparent' }
        });

        // Title
        slide.addText(title, {
          x: 0.7,
          y: yPos,
          w: 8.75,
          h: 0.4,
          fontSize: 18,
          color: colors.textMain,
          bold: true,
          valign: 'top',
          fontFace: 'Arial'
        });

        // Content with links
        addTextWithLinks(slide, value, {
          x: 0.7,
          y: yPos + 0.5,
          w: 8.75,
          h: itemHeight - 0.5,
          fontSize: 12,
          color: colors.textBody,
          valign: 'top',
          wrap: true,
          lineSpacing: 18,
          fontFace: 'Arial'
        });

        yPos += itemHeight;
      });
    }
  }

  // Save
  await pptx.writeFile({ fileName });
}

export default exportGetWhoToBuyPptx2;
